import { Address, ChainKey, getWallet, WalletKey } from "@silo/types";
import {
  AlertIcon,
  CheckmarkIcon,
  ExternalLinkIcon,
  LoaderIcon,
} from "../../icons";
import { cn } from "../../utils/class-names";
import { formatAddress, getAccountUrl, getTxUrl } from "@silo/utils";
import { Button } from "../button";
import { Avatar } from "./avatar";
import { CopyButton } from "../copy-button";
import { match } from "ts-pattern";
import { AccountDetailsTransaction } from "./types";

export interface AccountDetailsProps {
  className?: string;
  isImpersonating: boolean;
  account: Address;
  ensName: string | undefined;
  disconnect: () => void;
  change: () => void;
  activeWalletKey: WalletKey;
  chainKey: ChainKey;
  recentTransactions: AccountDetailsTransaction[];
  clearTransactions: () => void;
}

export function AccountDetails({
  className,
  isImpersonating,
  account,
  ensName,
  disconnect,
  change,
  activeWalletKey,
  chainKey,
  recentTransactions,
  clearTransactions,
}: AccountDetailsProps): JSX.Element {
  const isNotImpersonating = !isImpersonating;
  const displayAccount = ensName ?? formatAddress(account);
  const wallet = getWallet(activeWalletKey);
  const explorerLink = getAccountUrl(chainKey, account);

  return (
    <div className={cn("flex flex-col gap-10 w-full", className)}>
      <h2 className="text-xl font-medium text-white">Account</h2>

      {isNotImpersonating && (
        <div className="flex flex-col items-start gap-5 sm:items-center sm:flex-row">
          <p>Connected with {wallet.label}</p>

          <div className="flex gap-6 text-sm font-medium">
            <button
              className="text-base text-blue-300 hover:text-blue-200"
              onClick={disconnect}
            >
              Disconnect
            </button>

            <button
              className="text-base text-blue-300 hover:text-blue-200"
              onClick={change}
            >
              Change
            </button>
          </div>
        </div>
      )}

      {isImpersonating && (
        <div className="flex flex-col items-start gap-5">
          <div className="flex gap-2.5 text-amber-300">
            <span>
              <AlertIcon className="w-6 h-6" />
            </span>
            <p>
              {" "}
              You are impersonating user {displayAccount}. The app is read-only,
              and transactions will not work.
            </p>
          </div>

          <Button
            variant="warning"
            onClick={disconnect}
            className="self-stretch sm:self-start"
          >
            Disconnect
          </Button>
        </div>
      )}

      <div className="rounded-lg bg-shale-700 px-4 py-2.5 text-shale-200">
        <div className="flex items-center justify-between w-full max-w-full">
          <div className="flex gap-2 max-w-[85%]">
            <Avatar account={account} walletKey={activeWalletKey} />

            <span className="max-w-full overflow-hidden text-white overflow-ellipsis">
              {displayAccount}
            </span>
          </div>

          <span className="flex items-center gap-2 text-white">
            <CopyButton toCopy={account} iconClassName="w-6 h-6" />
            <a
              href={explorerLink}
              target="_blank"
              rel="noreferrer"
              className=" hover:underline"
            >
              <ExternalLinkIcon className="w-6 h-6" />
            </a>
          </span>
        </div>
      </div>

      {!!recentTransactions.length && (
        <div className="flex flex-col gap-10">
          <div className="flex items-center justify-between gap-5 sm:justify-start">
            <h3 className="text-lg font-medium">Recent transactions</h3>
            <button
              className="text-base font-medium text-blue-300"
              onClick={clearTransactions}
            >
              Clear all
            </button>
          </div>

          <div className="flex flex-col gap-4">
            {recentTransactions.map((transaction) => (
              <TransactionDisplay
                key={transaction.txHash}
                transaction={transaction}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface TransactionDisplayProps {
  className?: string;
  transaction: AccountDetailsTransaction;
}

function TransactionDisplay({
  className,
  transaction,
}: TransactionDisplayProps): JSX.Element {
  const { chainKey, txHash, message, status } = transaction;
  const explorerLink = getTxUrl(chainKey, txHash);

  return (
    <div
      className={cn(
        "flex items-center justify-between text-shale-200",
        className
      )}
    >
      <a
        href={explorerLink}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-3 hover:underline"
        data-cy="transaction-display-link"
      >
        <span className="text-shale-200">{message}</span>
        <ExternalLinkIcon className="w-5 h-5 text-white" />
      </a>

      {match(status)
        .with("pending", () => (
          <LoaderIcon className="w-5 h-5 text-amber-300 animate-spin" />
        ))
        .with("success", () => <CheckmarkIcon className="w-5 h-5 text-white" />)
        .with("error", () => <AlertIcon className="w-5 h-5 text-white" />)
        .exhaustive()}
    </div>
  );
}
